/**
 * Coordinate conversion service
 * Centralizes all coordinate system transformations between:
 * - Mapbox (lng/lat, Mercator)
 * - Three.js (x, y, z in meters)
 * - Application (feet for user-facing values)
 */

import * as THREE from 'three'
import mapboxgl from 'mapbox-gl'
import {
  FEET_TO_METERS,
  METERS_TO_FEET,
  positionFeetToMeters,
  positionMetersToFeet
} from '@/constants/units'

export class CoordinateService {
  /**
   * Convert a single value from feet to meters
   */
  static feetToMeters(feet: number): number {
    return feet * FEET_TO_METERS
  }

  /**
   * Convert a single value from meters to feet
   */
  static metersToFeet(meters: number): number {
    return meters * METERS_TO_FEET
  }

  /**
   * Convert position array from feet to meters
   */
  static positionFeetToMeters(position: [number, number, number]): [number, number, number] {
    return positionFeetToMeters(position)
  }

  /**
   * Convert position array from meters to feet
   */
  static positionMetersToFeet(position: [number, number, number]): [number, number, number] {
    return positionMetersToFeet(position)
  }

  /**
   * Create a Mapbox transformation matrix for a Three.js scene
   * Handles the coordinate system differences between Mapbox and Three.js
   *
   * @param center Map center coordinates [lng, lat]
   * @param altitude Scene altitude in meters
   * @returns Transformation matrix for the Three.js scene
   */
  static createMapboxTransformMatrix(
    center: [number, number],
    altitude: number
  ): THREE.Matrix4 {
    // Use Mapbox's projection to get world coordinates
    const modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
      center,
      altitude
    )

    // Scale the entire scene to match Mapbox coordinate system
    const scale = modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()

    // Create rotation matrix to align Three.js with Mapbox coordinate system
    // After transform: +X = east, +Y = up, +Z = south
    const rotationX = new THREE.Matrix4().makeRotationAxis(
      new THREE.Vector3(1, 0, 0),
      Math.PI / 2
    )

    // Negative Y scale compensates for Mapbox VP matrix Y-axis flip
    // (Mercator Y increases southward, screen Y increases upward)
    const scaleMatrix = new THREE.Matrix4().makeScale(scale, -scale, scale)

    const translationMatrix = new THREE.Matrix4().makeTranslation(
      modelAsMercatorCoordinate.x,
      modelAsMercatorCoordinate.y,
      modelAsMercatorCoordinate.z || 0
    )

    // Compose transformation: Translation * Scale * Rotation
    // (matches official Mapbox + Three.js integration pattern)
    const transformMatrix = new THREE.Matrix4()
    transformMatrix.multiply(translationMatrix)
    transformMatrix.multiply(scaleMatrix)
    transformMatrix.multiply(rotationX)

    return transformMatrix
  }

  /**
   * Calculate distance between two positions in meters
   * @param pos1 First position in feet
   * @param pos2 Second position in feet
   * @returns Distance in meters
   */
  static calculateDistance(
    pos1: [number, number, number],
    pos2: [number, number, number]
  ): number {
    const x1 = pos1[0] * FEET_TO_METERS
    const z1 = pos1[2] * FEET_TO_METERS
    const x2 = pos2[0] * FEET_TO_METERS
    const z2 = pos2[2] * FEET_TO_METERS

    const dx = x2 - x1
    const dz = z2 - z1

    return Math.sqrt(dx * dx + dz * dz)
  }

  /**
   * Convert lat/lng coordinates to scene-relative position in feet
   * Calculates offset from a reference point (locationCenter) and returns position in feet
   *
   * @param coordinates [lng, lat] target coordinates
   * @param locationCenter [lng, lat] reference point (scene origin)
   * @returns [x, y, z] position in feet relative to locationCenter
   */
  static lngLatToScenePosition(
    coordinates: [number, number],
    locationCenter: [number, number]
  ): [number, number, number] {
    const [lng, lat] = coordinates
    const [centerLng, centerLat] = locationCenter

    // Convert both points to Mercator coordinates
    const targetMercator = mapboxgl.MercatorCoordinate.fromLngLat([lng, lat], 0)
    const centerMercator = mapboxgl.MercatorCoordinate.fromLngLat([centerLng, centerLat], 0)

    // Calculate offset in Mercator units
    const offsetX = targetMercator.x - centerMercator.x
    const offsetY = targetMercator.y - centerMercator.y

    // Convert to meters using the scale at this location
    const scale = targetMercator.meterInMercatorCoordinateUnits()
    const metersX = offsetX / scale
    const metersZ = offsetY / scale // Scene +Z = south aligns with Mercator +Y = south

    // Convert from meters to feet
    const feetX = metersX * METERS_TO_FEET
    const feetZ = metersZ * METERS_TO_FEET

    return [feetX, 0, feetZ]
  }

  /**
   * Convert scene position in feet to lat/lng coordinates
   *
   * @param position [x, y, z] position in feet relative to center
   * @param locationCenter [lng, lat] reference point (scene origin)
   * @returns [lng, lat] geographic coordinates
   */
  static scenePositionToLngLat(
    position: [number, number, number],
    locationCenter: [number, number]
  ): [number, number] {
    const [centerLng, centerLat] = locationCenter

    // Convert feet to meters
    const metersX = position[0] * FEET_TO_METERS
    const metersZ = position[2] * FEET_TO_METERS

    // Get center in Mercator coordinates
    const centerMercator = mapboxgl.MercatorCoordinate.fromLngLat([centerLng, centerLat], 0)
    const scale = centerMercator.meterInMercatorCoordinateUnits()

    // Convert meters to Mercator offset
    const offsetX = metersX * scale
    const offsetY = metersZ * scale // Scene +Z = south aligns with Mercator +Y = south

    // Calculate target Mercator coordinates
    const targetX = centerMercator.x + offsetX
    const targetY = centerMercator.y + offsetY

    // Convert back to lng/lat
    const targetMercator = new mapboxgl.MercatorCoordinate(targetX, targetY, 0)
    const lngLat = targetMercator.toLngLat()

    return [lngLat.lng, lngLat.lat]
  }

  // ============================================================================
  // CONVENIENCE METHODS (object-based mapCenter format)
  // ============================================================================

  /**
   * Convert GPS coordinates to world coordinates (feet from mapCenter)
   * Convenience wrapper that accepts object-based coordinates
   *
   * @param lat Latitude
   * @param lng Longitude
   * @param mapCenter Map center as { lat, lng } object
   * @returns { worldX, worldZ } position in feet relative to mapCenter
   */
  static gpsToWorld(
    lat: number,
    lng: number,
    mapCenter: { lat: number; lng: number }
  ): { worldX: number; worldZ: number } {
    const position = CoordinateService.lngLatToScenePosition(
      [lng, lat],
      [mapCenter.lng, mapCenter.lat]
    )
    return {
      worldX: position[0],
      worldZ: position[2],
    }
  }

  /**
   * Convert world coordinates (feet from mapCenter) to GPS
   * Convenience wrapper that accepts object-based coordinates
   *
   * @param worldX X position in feet (east-west)
   * @param worldZ Z position in feet (north-south)
   * @param mapCenter Map center as { lat, lng } object
   * @returns { lat, lng } geographic coordinates
   */
  static worldToGps(
    worldX: number,
    worldZ: number,
    mapCenter: { lat: number; lng: number }
  ): { lat: number; lng: number } {
    const lngLat = CoordinateService.scenePositionToLngLat(
      [worldX, 0, worldZ],
      [mapCenter.lng, mapCenter.lat]
    )
    return {
      lat: lngLat[1],
      lng: lngLat[0],
    }
  }
}
